import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Globe, School, X, ArrowLeftRight, MessageSquare } from 'lucide-react';
import { db, auth, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  getDoc,
  doc,
  addDoc
} from 'firebase/firestore';
import { useIdentity } from '../context/IdentityContext';

const anonymousChatSetupCache = {
  userCollege: 'Unknown',
  isInitialized: false,
};

export const AnonymousChatSetupPage = ({ onToggleMobileSavedChats }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletCtx = useOutletContext() || {};
  const handleToggleMobileSavedChats = onToggleMobileSavedChats || outletCtx.onToggleMobileSavedChats;
  const { identityMode } = useIdentity();
  const defaultMode = location.state?.defaultMode || 'campus';
  
  const [selectedMode, setSelectedMode] = useState(defaultMode);
  const [userCollege, setUserCollege] = useState(anonymousChatSetupCache.userCollege);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!anonymousChatSetupCache.isInitialized);

  useEffect(() => {
    const fetchUserCollege = async () => {
      const user = auth.currentUser;
      if (!user) return navigate('/login');
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const college = userDoc.data()?.college || 'Unknown';
        setUserCollege(college);
        anonymousChatSetupCache.userCollege = college;
        anonymousChatSetupCache.isInitialized = true;
        
        if (defaultMode === 'campus' && college !== 'Unknown') {
            setSelectedMode('campus');
        } else {
            setSelectedMode(defaultMode);
        }
      } catch (e) {
      } finally {
        setIsInitializing(false);
      }
    };
    if (!anonymousChatSetupCache.isInitialized) {
      fetchUserCollege();
    }
  }, [navigate, defaultMode]);

  useEffect(() => {
    const checkActiveSession = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const q1 = query(collection(db, 'anonymousChats'), where('user1', '==', user.uid), where('ended', '==', false), where('saved', '==', false));
      const q2 = query(collection(db, 'anonymousChats'), where('user2', '==', user.uid), where('ended', '==', false), where('saved', '==', false));
      
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      
      if (!snap1.empty || !snap2.empty) {
        const activeChat = !snap1.empty ? snap1.docs[0] : snap2.docs[0];
        navigate(`/chat/${activeChat.id}`, { state: { ...location.state } });
      }
    };
    
    if (!isInitializing && !location.state?.autoSearch) {
      checkActiveSession();
    }
  }, [isInitializing, navigate, selectedMode, userCollege, location.state]);

  const handleSlayPress = async () => {
    if (isLoading || isInitializing) return;
    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) return navigate('/login');
      
      const queueRef = collection(db, 'anonymousChatQueueV2');
      
      const existingQ = query(queueRef, where('uid', '==', user.uid));
      const existingSnap = await getDocs(existingQ);
      
      if (existingSnap.empty) {
          await addDoc(queueRef, {
              uid: user.uid,
              mode: selectedMode,
              college: userCollege,
              timestamp: serverTimestamp(),
          });
          
          logEvent(analytics, 'join_queue', { 
              type: '1-1_text', 
              initial_mode: selectedMode 
          });
      }
      navigate('/chat/searching', { state: { selectedMode, userCollege } });
    } catch (e) {
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing && !location.state?.autoSearch) {
     return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-[#0a0a0a]">
            <div className="w-8 h-8 rounded-full border-t-2 border-b-2 border-[#0BD9E5] animate-spin"></div>
        </div>
     );
  }

  if (identityMode === 'real') {
     return (
       <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
          <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
             <MessageSquare size={32} />
          </div>
          <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center">Chat</h2>
          <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
       </main>
     );
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-[#0a0a0a] pb-28 sm:pb-6">
      
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-[#0BD9E5]/10 blur-[120px] rounded-full mix-blend-screen" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-500/10 blur-[120px] rounded-full mix-blend-screen" />
      </div>

      <div className="relative z-10 flex flex-col h-full p-6 mx-auto w-full max-w-md">
          
          <div className="flex items-center justify-between mb-8 mt-4 relative z-50">
              <div className="w-10"></div>
              <div className="px-4 py-1.5 rounded-full bg-[#111] border border-gray-800 flex items-center gap-2">
                 <MessageSquare size={14} className="text-[#0BD9E5]" />
                 <span className="text-xs font-bold tracking-widest uppercase">1-1 Chat</span>
              </div>
              {handleToggleMobileSavedChats ? (
                <button 
                  onClick={handleToggleMobileSavedChats}
                  title="View Saved Connections"
                  className="sm:hidden w-10 h-10 flex items-center justify-center rounded-full bg-[#111] border border-gray-800 text-gray-300 hover:text-white transition-colors"
                >
                  <MessageSquare size={18} className="text-[#0BD9E5]" />
                </button>
              ) : (
                <div className="w-10"></div>
              )}
          </div>

          <div className="flex-1 flex flex-col items-center justify-center -mt-10 w-full">
              <h2 className="text-4xl font-black mb-10 tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 text-center">
                  Select Mode
              </h2>

              <div className="grid grid-cols-2 md:flex-row gap-3 md:gap-5 w-full">
                  <button 
                      onClick={() => setSelectedMode('campus')}
                      className={`relative w-full p-4 sm:p-8 rounded-2xl sm:rounded-3xl border text-left transition-all overflow-hidden ${selectedMode === 'campus' ? 'border-[#0BD9E5] bg-[#0BD9E5]/10 scale-[1.02]' : 'border-gray-800 bg-[#111] hover:border-gray-600 hover:bg-[#1a1a1a]'}`}
                  >
                      <School className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-4 ${selectedMode === 'campus' ? 'text-[#0BD9E5]' : 'text-gray-500'}`} />
                      <h3 className={`text-lg sm:text-2xl font-bold mb-1 sm:mb-2 ${selectedMode === 'campus' ? 'text-white' : 'text-gray-300'}`}>Campus</h3>
                      <p className={`text-xs sm:text-sm ${selectedMode === 'campus' ? 'text-gray-300' : 'text-gray-500'}`}>
                          Talk with verified students from <span className="text-[#0BD9E5] font-semibold block sm:inline">{userCollege === 'Unknown' ? 'your college' : userCollege}</span>
                      </p>
                  </button>

                  <button 
                      onClick={() => setSelectedMode('global')}
                      className={`relative w-full p-4 sm:p-8 rounded-2xl sm:rounded-3xl border text-left transition-all overflow-hidden ${selectedMode === 'global' ? 'border-[#BD00FF] bg-[#BD00FF]/10 scale-[1.02]' : 'border-gray-800 bg-[#111] hover:border-gray-600 hover:bg-[#1a1a1a]'}`}
                  >
                      <Globe className={`w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-4 ${selectedMode === 'global' ? 'text-[#BD00FF]' : 'text-gray-500'}`} />
                      <h3 className={`text-lg sm:text-2xl font-bold mb-1 sm:mb-2 ${selectedMode === 'global' ? 'text-white' : 'text-gray-300'}`}>Global</h3>
                      <p className={`text-xs sm:text-sm ${selectedMode === 'global' ? 'text-gray-300' : 'text-gray-500'}`}>
                          Connect with students across the world <br className="hidden sm:block"/>
                          <span className="text-[#BD00FF] font-semibold text-[10px] sm:text-[11px] uppercase tracking-wider block mt-1 sm:mt-2">
                              (Colleges Worldwide)
                          </span>
                      </p>
                  </button>
              </div>
          </div>

          <motion.div className="mt-auto pt-8 pb-24 sm:pb-6 flex justify-center w-full">
              <button 
                  onClick={handleSlayPress}
                  disabled={isLoading || (selectedMode === 'campus' && userCollege === 'Unknown')}
                  className="w-full h-16 bg-white text-black font-bold uppercase tracking-widest rounded-xl hover:bg-gray-200 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                  {isLoading ? 'INITIATING...' : (
                     <>
                        Slay the Chat <Zap size={20} className="fill-current text-black" strokeWidth={0} />
                     </>
                  )}
              </button>
          </motion.div>
      </div>
    </main>
  );
};

export default AnonymousChatSetupPage;
